<script setup lang="ts">
// icons
import { faFrown } from '@fortawesome/pro-light-svg-icons';

// utils
import { fixImageUrl } from '@/utils/general';
import { RBN_COUNTRY_SEARCH, RBN_FEDERAL_STATE_SEARCH, RBN_CAMPSITE_DETAIL } from '@/utils/constants';

// components
import CiReviewFooter from '@/components/CiReview/CiReviewFooter.vue';
import CiReviewHeader from '@/components/CiReview/CiReviewHeader.vue';
import CiSkeletonItem from '@/components/skeleton/CiSkeletonItem.vue';
import CiSkeletonReviews from '@/components/skeleton/CiSkeletonReviews.vue';

/********************
 * COMPOSITIONS      *
 ********************/
const backendStore = useBackendStore();
const campsiteStore = useCampsiteStore();
const localePath = useLocalePath();
const reviewService = useReviewService();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const seoStore = useSeoStore();
const userStore = useUserStore();
const viewport = useViewport();
const { $gettext } = useGettext();
const { dynamicHeadline } = useDynamicHeadline();
const { fetchError, fetchFailed, fetchStatus } = useFetchError();
const { getDescription } = useCampsiteDetailDescription({ campsite: () => campsiteStore.campsite });
const { locale } = useI18n();
const { mapRating } = useRating();

/********************
 * REFS & VARS       *
 ********************/
const doneWithToken = ref(false);
const ignoreUrlIdForScrolling = ref(false);
const limit = ref(20);
const loadingMore = ref(false);
const offset = ref(0);
const reset = ref(false);
const reviewRefs = reactive<{ [key: string]: HTMLElement }>({});
const reviews = ref([]);
const selectedGroup = ref<string | null>(null);

const { data, pending, refresh, error } = await useLazyAsyncData(`review-wrapper-${route.params?.identifier}-${route.params?.id}`, async (): Promise<{ campsite: any; reviews: any; reviewsInitiallyLoaded: boolean }> => {
  // get campsite
  let campsite = campsiteStore.campsite || null;
  try {
    if (!campsite || campsite.slug !== route.params.identifier) {
      campsite = await campsiteStore.getCampsite({ slug: route.params.identifier, lang: locale.value });
    }
    // handle meta params
    const imgCount
      = (campsite?.media_count?.pictures?.campsite || 0)
        + (campsite?.media_count?.pictures?.visitor || 0);

    seoStore.setDynamicMetaParam({
      campsite_federalstate: campsite?.meta?.federal_state?.name,
      campsite_imagecount: isNaN(imgCount) ? 0 : imgCount,
      campsite_reviewcount: campsite?.rating_guest?.rating_count || 0,
      campsite_city: campsite?.meta?.city_obj?.name,
      campsite_country: campsite?.meta?.country?.name,
      campsite_name: campsite?.meta?.name,
      campsite_rating: campsite?.rating_campsite?.rating_avg_overall || 0,
      custom_description: getDescription(),
    });
  } catch (error) {
    fetchError(error);
    return error;
  }

  let reviewsInitiallyLoaded: boolean;
  try {
    (() => data.value)();
    reviewsInitiallyLoaded = data.value?.reviewsInitiallyLoaded === true;
  } catch (_e) {
    reviewsInitiallyLoaded = false;
  }

  const reviewId = (reviewsInitiallyLoaded && !reset.value) ? null : route.params?.id || null;

  // get reviews
  let localReviews;
  try {
    doneWithToken.value = userStore.loggedIn;
    localReviews = await reviewService.getReviews(locale.value, route.params?.identifier as string, {
      limit: limit.value,
      offset: offset.value,
      id: reviewId,
    });
  } catch (error) {
    fetchError(error);
    return error;
  }
  return { campsite, reviews: localReviews, reviewsInitiallyLoaded: true };
});

if (error.value) {
  throw createError({ statusCode: error.value?.statusCode, message: error.value.statusMessage });
}

/********************
 * REFS & VARS       *
 ********************/
const jsonLd = computed(() => {
  if (data.value?.campsite === null || !data.value?.campsite?.meta) {
    return null;
  }

  let imagePath = data.value?.campsite.media.images.length
    ? fixImageUrl(backendStore.url, data.value?.campsite.media.images[0].url)
    : 0;

  if (!imagePath) {
    imagePath = `${runtimeConfig.public.baseUrl}/img/campsite-fallback.png`;
  }

  const metadata = {
    '@context': 'http://schema.org/',
    '@type': 'Campground',
    '@id': '#campsite',
    'name': data.value.campsite.meta.name,
    'image': imagePath,
    'priceRange': formattedPrice.value,
    'address': {
      '@type': 'PostalAddress',
      'addressCountry': data.value.campsite.meta.country.name,
      'addressLocality': data.value.campsite.meta.name,
      'addressRegion': data.value.campsite.meta.federal_state.name,
      'postalCode': data.value.campsite.meta.zip,
      'streetAddress': `${data.value.campsite.meta.street} ${data.value.campsite.meta.street_no}`,
    },
  };

  const ratingCount = data.value?.campsite.rating_guest.rating_count;

  if (ratingCount) {
    metadata.aggregateRating = {
      '@type': 'AggregateRating',
      ratingCount,
      'ratingValue': data.value?.campsite.rating_campsite.rating_avg_overall || 0.0,
    };
  }
  return metadata;
});

const breadcrumb = computed(() => {
  const breadcrumb = [];

  if (data.value?.campsite?.meta.country) {
    breadcrumb.push({
      name: data.value.campsite.meta.country.name,
      class: 'breadcrumb-item--chevron',
      to: localePath({
        name: RBN_COUNTRY_SEARCH,
        params: {
          countrySlug: data.value.campsite.meta.country.slugs[locale.value],
        },
      }),
    });
  }

  if (data.value?.campsite?.meta.federal_state) {
    breadcrumb.push({
      name: data.value.campsite.meta.federal_state.name,
      class: 'breadcrumb-item--chevron',
      to: localePath({
        name: RBN_FEDERAL_STATE_SEARCH,
        params: {
          countrySlug: data.value.campsite.meta.country.slugs[locale.value],
          federalStateSlug: data.value.campsite.meta.federal_state.slugs[locale.value],
        },
      }),
    });
  }

  breadcrumb.push({
    name: data.value?.campsite?.meta.name,
    class: 'breadcrumb-item--chevron',
    to: localePath({
      name: RBN_CAMPSITE_DETAIL,
      params: {
        identifier: data.value.campsite.slug,
      },
    }),
  });

  breadcrumb.push({
    name: $gettext('Reviews'),
    class: 'breadcrumb-item--chevron',
  });

  return breadcrumb;
});

const formattedPrice = computed(() => {
  if (!data.value?.campsite?.price) {
    return null;
  }
  const price = data.value.campsite.price.price_off_season || data.value.campsite.price.price1;
  if (!price) {
    return null;
  }
  return `${price.toFixed(2)} EUR`;
});

const reviewsToLoad = computed(() => {
  const unseen = data.value?.campsite?.rating_guest?.rating_count - reviews.value?.length;
  return unseen <= limit.value ? unseen : limit.value;
});

const i18nLoadMoreButton = computed(() => {
  return loadingMore.value ? $gettext('Please wait…') : $gettext('Load more');
});

/********************
 * WATCHER           *
 ********************/
watch(
  () => data.value?.reviews,
  (newValue, oldValue) => {
    if (newValue) {
      if (newValue?.next === oldValue?.next && newValue?.previous === oldValue?.previous) {
        return;
      }

      if (reset.value) {
        reviews.value = toRaw(newValue.results);
      } else {
        appendToReviews(newValue.results);
        loadingMore.value = false;
      }
      if (route.params?.id) {
        scrollToReview(route.params.id);
      }
    }
  },
  { immediate: true },
);

watch(
  () => data.value?.campsite.rating_guest?.rating_count,
  (newValue) => {
    if (newValue !== null && newValue !== undefined) {
      const robots = newValue === 0 ? 'NOINDEX' : 'INDEX';
      useHead({
        meta: [
          {
            key: 'robots',
            name: 'robots',
            content: robots,
            tagDuplicateStrategy: 'replace',
          },
        ],
      });
    }
  },
  { immediate: true },
);

/********************
 * HOOKS             *
 ********************/
onMounted(() => {
  if (userStore.loggedIn && !doneWithToken.value) {
    // if user is logged in, we need to delete all reviews and fetch them again
    // with token to see user specific review infos
    reset.value = true;
    if (!pending.value && reviews.value.length) {
      setTimeout(() => {
        refresh();
      }, 0);
    }
  }
});

if (jsonLd.value) {
  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(jsonLd.value),
      },
    ],
  });
}

/********************
 * FUNCTIONS         *
 ********************/
async function loadMore() {
  offset.value = reviews.value.length;
  loadingMore.value = true;

  if (route.params.id && !ignoreUrlIdForScrolling.value) {
    ignoreUrlIdForScrolling.value = true;
  }
  reset.value = false;
  await refresh();
}

function appendToReviews(newValue: any) {
  const newReviews: any[] = [];
  toRaw(newValue).forEach((review: any) => {
    if (!reviews.value.find(r => r.id === review.id)) {
      newReviews.push(review);
    }
  });
  reviews.value?.push(...newReviews);
}

function onToggleAccordion(id: string) {
  selectedGroup.value = selectedGroup.value === id ? null : id;
}

function scrollToReview(id: string) {
  if (ignoreUrlIdForScrolling.value) {
    return;
  }

  setTimeout(() => {
    if (reviewRefs[id]) {
      reviewRefs[id].scrollIntoView({ block: 'start', behavior: 'smooth' });
      selectedGroup.value = id;
    }
  }, 200);
}

function onMarkedAsHelpful(review) {
  review.helpful += 1;
  review.is_rateable = false;
  review.is_rated_by_user = true;
}
</script>

<template>
  <div
    v-if="!fetchFailed"
    class="relative mb-12"
  >
    <CiSkeletonReviews v-if="(pending && !reset && offset === 0) || (pending && !reviews?.length)" />

    <div
      v-else-if="data?.campsite"
      class="flex flex-col"
    >
      <div
        v-if="viewport.isLessThan('lg')"
        class="header bg-gray-10"
      >
        <div class="container-fluid pb-12 pt-2">
          <div class="flex h-[76px] items-center md:h-[50px]">
            <CiNavigationToggle
              type="black"
              :href="localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: data?.campsite?.slug } })"
            />
            <h1 class="mb-0 ml-2 text-base font-normal">
              {{ dynamicHeadline }}
            </h1>
          </div>
        </div>
      </div>

      <div class="container order-1 max-lg:!max-w-full lg:order-2">
        <div
          v-if="data?.campsite"
          class="relative -top-7 grid grid-cols-12 md:-top-10 lg:-top-12"
        >
          <!-- campsite rating -->
          <div class="col-span-12 lg:col-span-2">
            <div class="mx-auto w-4/12 md:w-3/12 lg:w-10/12">
              <CiRatingRadial
                :rating="data.campsite.rating_campsite.rating_avg_overall"
                class="chart-wrapper chart-wrapper--border-lg mx-auto block w-full"
                font-class="h4"
              />
            </div>
            <div
              v-if="data.campsite.rating_guest.rating_count"
              class="mt-2 flex justify-center text-center font-medium uppercase"
            >
              {{ mapRating(data.campsite.rating_campsite.rating_avg_overall) }}
            </div>
            <div class="flex justify-center">
              <span
                v-if="data.campsite.rating_guest.rating_count"
                class="text-small-book"
              >
                {{
                  $ngettext(
                    '%{count} rating',
                    '%{count} ratings',
                    data.campsite.rating_guest.rating_count,
                    { count: data.campsite.rating_guest.rating_count },
                  )
                }}
              </span>
              <span
                v-else
                class="text-small-book"
              >{{ $gettext('No ratings yet') }}</span>
            </div>
          </div>

          <!-- campsite title & region breadcrumb -->
          <h1 class="col-span-12 mb-0 hidden items-end text-center lg:col-span-10 lg:flex lg:text-left">
            {{ dynamicHeadline }}
          </h1>
        </div>

        <!-- reviews -->
        <template
          v-if="reviews?.length"
        >
          <ul class="m-0 -mx-4 !p-0 lg:mx-0">
            <li
              v-for="(review, index) in reviews"
              :key="review.id"
              :ref="el => (reviewRefs[review.id] = el as HTMLElement)"
              :class="{ 'mt-0 lg:mt-6': index !== 0 }"
              class="review__item relative overflow-hidden rounded-none border-0 lg:rounded lg:border lg:border-[#dee2e6] lg:bg-gray-10"
            >
              <CiAccordion
                :show="selectedGroup === review.id"
                :mq="'lg'"
                layout="primary"
                @accordion-toggle="onToggleAccordion(review.id)"
              >
                <template #header>
                  <div
                    v-if="viewport.isLessThan('lg')"
                    class="footer__headline py-2"
                  >
                    <CiReviewHeader
                      :review="review"
                      avatar-size="xl"
                      is-mobile
                    />
                  </div>
                </template>
                <div class="container grid grid-cols-3 lg:gap-x-[30px]">
                  <div class="z-20 col-span-3 lg:col-span-1">
                    <div v-if="viewport.isGreaterOrEquals('lg')">
                      <CiReviewHeader
                        :review="review"
                        avatar-size="xl"
                      />
                    </div>
                  </div>
                  <div class="col-span-3 lg:col-span-2 lg:mt-6">
                    <CiReview :review="review" />
                  </div>
                  <div
                    class="col-span-3 mx-[-15px] grid grid-cols-3 px-[15px] lg:gap-x-[30px]"
                    :class="{ 'bg-white': viewport.isGreaterOrEquals('lg') }"
                  >
                    <div class="col-span-3 lg:col-span-2 lg:col-start-2">
                      <CiReviewFooter :review="review" @marked-as-helpful="onMarkedAsHelpful(review)" />
                    </div>
                  </div>
                </div>
              </CiAccordion>
            </li>
          </ul>
        </template>

        <!-- load more skeleton -->
        <template v-if="loadingMore">
          <CiSkeletonItem
            v-for="(_, idx) in reviewsToLoad"
            :key="idx"
            class="mt-0 h-[76px] w-full border-b border-[#adb5bd] lg:mt-6 lg:h-[450px] lg:border-b-0"
          />
        </template>

        <!-- no reviews -->
        <div
          v-if="data?.reviews?.count === 0"
          class="container"
        >
          <div class="reviews__no-reviews flex flex-row items-center justify-center p-6">
            <CiAwesomeIcon
              :icon="faFrown"
              class="mr-4 fill-dark"
              ratio="2"
            />
            <h3 class="mt-4 text-center font-normal" style="font-size: 1.5rem;">
              {{ $gettext("We're sorry. There are no reviews yet. Be the first!") }}
            </h3>
          </div>
        </div>

        <!-- load more button -->
        <template v-if="(!loadingMore && reviewsToLoad) || (loadingMore && reviewsToLoad >= limit)">
          <div class="mx-auto mb-12 mt-6 w-full lg:w-1/2">
            <CiButton
              tag="button"
              variant="secondary"
              class="w-full"
              :disabled="loadingMore"
              @click="loadMore"
            >
              {{ i18nLoadMoreButton }}
            </CiButton>
          </div>
        </template>
      </div>

      <!-- breadcrumb -->
      <div
        v-if="breadcrumb && breadcrumb.length"
        class="order-2 pb-12 pt-6 lg:order-1"
        :class="{ 'bg-gray-10': !viewport.isLessThan('lg') }"
      >
        <div class="container">
          <CiBreadcrumb
            :items="breadcrumb"
            class="mb-6"
            :background="viewport.isLessThan('lg') ? 'white' : 'gray'"
          />
        </div>
      </div>
    </div>
  </div>

  <CiErrorView
    v-else
    :error="fetchStatus"
  />
</template>

<style lang="postcss" scoped>
.review__item::before {
  @apply hidden lg:absolute lg:block lg:left-0 lg:right-0 lg:top-0 lg:w-full lg:h-8 lg:z-0 lg:content-[''] lg:bg-gray-30;
}

:deep(.accordion) {
  &.open {
    .review__date-of-stay {
      @apply text-white lg:text-info;
    }

    .accordion__body {
      @apply bg-gray-10 lg:bg-transparent;
    }
  }
}
</style>
